import TemplateSelectionStep from '@components/templates/templateSelectionStep';
import TimerTypeSelectionStep from '@components/templates/timerTypeSelectionStep';
import { TimerTemplateId } from '@lib/timerTemplates';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@nextui-org/react';
import { useEffect, useState } from 'react';
import { TimerType } from '@/types';

type WizardStep = 'timer-type' | 'template';

export interface TimerCreationWizardSelection {
	timerType: TimerType;
	templateId: string;
}

interface TimerCreationWizardModalProps {
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	onSelect: (selection: TimerCreationWizardSelection) => void;
}

/** Step 1 picks the timer type, step 2 the template; the Select button opens the timer editor with them. */
export default function TimerCreationWizardModal({ isOpen, onOpenChange, onSelect }: TimerCreationWizardModalProps) {
	const [step, setStep] = useState<WizardStep>('timer-type');
	const [timerType, setTimerType] = useState<TimerType>(TimerType.BAR);
	const [selectedTemplateId, setSelectedTemplateId] = useState<TimerTemplateId | null>(null);

	useEffect(() => {
		if (!isOpen) {
			setStep('timer-type');
			setTimerType(TimerType.BAR);
			setSelectedTemplateId(null);
		}
	}, [isOpen]);

	/** The template tiles are drawn in the shape of the timer type, so a new type starts with nothing selected. */
	const handleTimerTypeSelect = (selectedTimerType: TimerType) => {
		setTimerType(selectedTimerType);
		setSelectedTemplateId(null);
		setStep('template');
	};

	const handleTemplateConfirm = () => {
		if (selectedTemplateId) {
			onSelect({ timerType, templateId: selectedTemplateId });
		}
	};

	return (
		<Modal isOpen={isOpen} onOpenChange={onOpenChange} backdrop='blur' scrollBehavior='inside' size='5xl'>
			<ModalContent>
				{(onClose) => (
					<>
						<ModalHeader>{step === 'timer-type' ? 'Step 1: Choose timer type' : 'Step 2: Select template'}</ModalHeader>
						<ModalBody className='border-y border-default-200 p-4 lg:p-8'>
							{step === 'timer-type' ? (
								<TimerTypeSelectionStep onSelect={handleTimerTypeSelect} />
							) : (
								<TemplateSelectionStep
									timerType={timerType}
									selectedTemplateId={selectedTemplateId}
									onSelect={setSelectedTemplateId}
								/>
							)}
						</ModalBody>
						{step === 'template' && (
							<ModalFooter className='flex items-center justify-between'>
								<Button color='default' variant='flat' onPress={() => setStep('timer-type')}>
									Back
								</Button>
								<div className='flex items-center gap-2'>
									<Button color='default' onPress={onClose}>
										Cancel
									</Button>
									<Button color='primary' isDisabled={!selectedTemplateId} onPress={handleTemplateConfirm}>
										Select
									</Button>
								</div>
							</ModalFooter>
						)}
					</>
				)}
			</ModalContent>
		</Modal>
	);
}
